<template>
  <div
    data-qa="service_list_content"
    class="px-2 container"
  >
    <h1
      class="text-h4 text--text mb-6"
      data-qa="column-content-h1"
    >
      Медицинские услуги
    </h1>
    <component
      :is="isMobile ? ServiceListMobile : ServiceListDesktop"
      :service-groups="serviceGroups"
      data-qa="service_list_content_list"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useContextStore } from 'src/store'
import formatPrice from 'src/functions/formatPrice'
import type {
  ContextStore,
  ServiceCategory,
  ServiceGroup,
} from 'src/components/pages/services/index/types'
import ServiceListDesktop from 'src/components/pages/services/index/components/ServiceListDesktop.vue'
import ServiceListMobile from 'src/components/pages/services/index/components/ServiceListMobile.vue'
// ToDo Заменить hardcode URLs в компоненте (таких URLs нет в 'urls-pd.json')

const { context }: ContextStore = useContextStore()

const isMobile = computed(() => context?.flavour === 'mobile')
const serviceGroups = computed<ServiceGroup[]>(() => {
  if (!context) {
    return []
  }

  const serviceGroupMap = new Map<string, {
    translit: string
    name: string
    serviceClassMap: Map<string, {
      href: string
      name: string
      group: string
      translit: string
      serviceCategoryMap: Map<string, ServiceCategory>
    }>
  }>()

  context.dtypes.forEach(dtype => {
    const serviceGroupKey = dtype.dclass.group

    if (!serviceGroupMap.has(serviceGroupKey)) {
      serviceGroupMap.set(serviceGroupKey, {
        translit: dtype.dclass.group,
        name: dtype.dclass.group === 'uslugi'
          ? 'Лечение'
          : 'Диагностика',
        serviceClassMap: new Map(),
      })
    }

    const { translit: serviceGroupTranslit, serviceClassMap } = serviceGroupMap.get(dtype.dclass.group)!
    const serviceClassKey = `${serviceGroupKey}-${dtype.dclass.name}`

    if (!serviceClassMap.has(serviceClassKey)) {
      serviceClassMap.set(serviceClassKey, {
        href: `https://${context.request.country.site.domain}/services/${dtype.dclass.group}/${dtype.dclass.translit}`,
        group: dtype.dclass.group,
        name: dtype.dclass.name,
        translit: dtype.dclass.translit,
        serviceCategoryMap: new Map(),
      })
    }

    const { translit: serviceClassTranslit, serviceCategoryMap } = serviceClassMap.get(serviceClassKey)!
    const serviceCategoryKey = dtype.category
      ? `${serviceClassKey}-${dtype.category.name}`
      : `${serviceClassKey}-empty`

    if (!serviceCategoryMap.has(serviceCategoryKey)) {
      serviceCategoryMap.set(serviceCategoryKey, {
        name: dtype.category ? dtype.category.name : '',
        href: dtype.category
          ? `https://${context.request.country.site.domain}/services/${serviceGroupTranslit}/${serviceClassTranslit}/${dtype.category.translit}`
          : '',
        serviceTypes: [],
      })
    }

    const serviceCategory = serviceCategoryMap.get(serviceCategoryKey)!

    serviceCategory.serviceTypes.push({
      name: dtype.name_short,
      price: formatPrice({
        price: dtype.price,
        currencySymbol: context.currency.symbol,
      }),
      href: `https://${context.request.country.site.domain}/services/${serviceGroupTranslit}/${dtype.translit}`,
    })
  })

  const sortByName = (a: { name: string }, b: { name: string }) => (
    a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'ru-RU')
  )
  const getFirstUpper = (text: any) => (
    typeof text === 'string'
      ? `${text.slice(0, 1).toUpperCase()}${text.slice(1)}`
      : ''
  )

  return [...serviceGroupMap.values()].map(serviceGroup => ({
    name: getFirstUpper(serviceGroup.name),
    translit: serviceGroup.translit,
    serviceClasses: [...serviceGroup.serviceClassMap.values()].map(serviceClass => ({
      href: serviceClass.href,
      name: getFirstUpper(serviceClass.name),
      group: serviceClass.group,
      translit: serviceClass.translit,
      serviceCategories: [...serviceClass.serviceCategoryMap.values()].map(serviceCategory => ({
        href: serviceCategory.href,
        name: getFirstUpper(serviceCategory.name),
        serviceTypes: serviceCategory.serviceTypes.map(serviceType => ({
          name: getFirstUpper(serviceType.name),
          price: serviceType.price,
          href: serviceType.href,
        }))
          .sort(sortByName),
      }))
        .sort(sortByName),
    }))
      .sort(sortByName),
  }))
    .sort(sortByName)
})
</script>

<style scoped lang="scss">
@import 'src/styles/index';
</style>
